const version = "v3"; // To update if/when version changes

export const urls = {
  // endpoints grouped by base api endpoint
  accounts: {
    API_SECURITY_SESSION: () => `accounts/${version}/security/session`,
    API_LOSS_LIMITS: () => `accounts/${version}/lossLimits`,
  },
  data: {
    API_SINGLE_COMPETITION: (competitionNodeId: string) => {
      return `data/${version}/competitions/${competitionNodeId}/events`;
    },
    API_SINGLE_COMPETITION_ASIAN: (competitionNodeId: string) => {
      return `data/${version}/asian/competitions/${competitionNodeId}/events`;
    },
    API_COMPETITIONS: () => `data/${version}/competitions/events`,
    API_ASIAN_COMPETITIONS: () => `data/${version}/asian/competitions/events`,
    API_LEAGUESELECTION: () =>
      `data/${version}/asian/competitions/leagueSelection`,
    API_FUTURES: () => {
      return `data/${version}/competitions/events/futures`;
    },
    API_FUTURES_COUNT: () => {
      return `data/${version}/competitions/events/futures/count`;
    },
    API_LIVE_COUNT: () => {
      return `data/${version}/competitions/events/liveCount`;
    },
    API_MARKET_DATA: () => {
      return `data/${version}/marketData`;
    },
    API_LANDING_PAGE: () => {
      return `data/${version}/marketData/landingPage`;
    },
    API_MARKET_GROUPINGS: sportId => {
      return `data/${version}/marketGroupingBySport/${sportId}`;
    },
    API_BOOKMARKS: () => {
      return `data/${version}/bookmarks`;
    },
    API_EVENTS_COMPETITIONS: () => {
      return `data/${version}/eventsCompetitions`;
    },
    API_SPORTS_SELECTION: () => {
      return `data/${version}/sportsNumberOfEvents`;
    },
    API_DAYS_WITH_EVENTS: () => {
      return `data/${version}/daysWithEvents`;
    },
    API_SPORTS: () => {
      return `data/${version}/sports`;
    },
    API_RUNNERS_MULTIPLIER_PRICES: (runnerId?: number) => {
      let url = `data/${version}/runners/multiplier/prices`;
      if (runnerId !== undefined) {
        url += `?runnerIds=${runnerId}`;
      }
      return url;
    },
    API_RUNNERS_PRICES: (runnerId?: number) => {
      let url = `data/${version}/runners/prices`;
      if (runnerId !== undefined) {
        url += `?runnerIds=${runnerId}`;
      }
      return url;
    },
    API_FAVOURITES: () => `data/${version}/eventFavourites`,
    API_NAVIGATION: () => `data/${version}/navigation`,
    API_ASIAN_EVENTS: () => `data/${version}/asian/events`,
    API_MOBILE_EVENTS: () => `data/${version}/events`,
  },
  bets: {
    API_BET_BUILDER: () => `betting/${version}/bets/betBuilder/calculateOdds`,
  },
  // betting
  // betting endpoints to go here when added
  // cashier
  cashier: {
    API_REGISTRATION_CURRENCIES: () => {
      return `cashier/${version}/currencies/publicRegistration`;
    },
    API_CRYPTO_CURRENCIES: () => {
      return `cashier/${version}/crypto/currencies`;
    },
    API_CRYPTO_DEPOSIT: () => {
      return `cashier/${version}/crypto/deposit`;
    },
    API_CRYPTO_QUOTE: () => {
      return `cashier/${version}/crypto/quote`;
    },
    API_CRYPTO_TRANSACTION: () => {
      return `cashier/${version}/crypto/transaction`;
    },
    API_CRYPTO_TRANSACTIONS: () => {
      return `cashier/${version}/crypto/transactions`;
    },
    API_CRYPTO_WITHDRAW_AUTOMATIC: () => {
      return `cashier/${version}/crypto/automaticWithdrawal`;
    },
    API_TURNOVER: () => {
      return `cashier/${version}/info/playerMonthlyTurnover`;
    },
    API_GATEWAYS: () => {
      return `cashier/${version}/gateways`;
    },
    API_GATEWAYS_GLOBAL: () => {
      return `cashier/${version}/gateways/global`;
    },
    API_DEPOSIT: (gateway: string) => {
      return `cashier/${version}/${gateway}/deposit`;
    },
    API_DEPOSIT_ASIAN: (gateway: string) => {
      return `cashier/${version}/asian/${gateway}/deposit`;
    },
    API_WITHDRAW: (gateway: string) => {
      return `cashier/${version}/${gateway}/withdraw`;
    },
    API_WITHDRAW_ASIAN: (gateway: string) => {
      return `cashier/${version}/asian/${gateway}/withdraw`;
    },
    API_VERIFY: () => {
      return `cashier/${version}/neteller/paysafe`;
    },
    API_VERIFY_ASIAN: () => {
      return `cashier/${version}/asian/neteller/paysafe`;
    },
    API_REJECT: () => {
      return `cashier/${version}/neteller/paysafe`;
    },
    API_REJECT_ASIAN: () => {
      return `cashier/${version}/asian/neteller/paysafe`;
    },
    API_NUVEI: (transactionType: string) => {
      return `cashier/${version}/nuvei/${transactionType}`;
    },
  },
  // public
  public: {
    API_REGISTRATION_UPDATE_LOSS_LIMITS_PROMPT: () => {
      return `public/registration/updateLossLimitsPrompt`;
    },
  },
  // prices
  // prices endpoints to go here when added
  // reports
  reports: {
    API_REPORTS_ALL: () => {
      return `/betting/${version}/eurasian/statement/reports/all`;
    },
    API_REPORTS_FUNDING: () => {
      return `/cashier/${version}/eurasian/statement/reports/fundings`;
    },
    API_REPORTS_FUNDING_BY_DATE: () => {
      return `/betting/${version}/eurasian/statement/reports/fundingsByDate`;
    },
    API_REPORTS_BETS: () => {
      return `/betting/${version}/eurasian/statement/reports/bets`;
    },
    API_REPORTS_BETS_BY_DATE: (runnerId?: number) => {
      return (
        `/betting/${version}/eurasian/statement/reports/betsByDate` +
        (runnerId ? `/${runnerId}` : "")
      );
    },
    API_REPORTS_BETS_BY_LEAGUE: (leagueId?: number) => {
      return (
        `/betting/${version}/eurasian/statement/reports/betsByLeague` +
        (leagueId ? `/${leagueId}` : "")
      );
    },
    API_REPORTS_BETS_BY_EVENT: (eventId?: number) => {
      return (
        `/betting/${version}/eurasian/statement/reports/betsByEvent` +
        (eventId ? `/${eventId}` : "")
      );
    },
    API_REPORTS_BETS_CURRENT: () => {
      return `/betting/${version}/eurasian/reports/bets/current`;
    },
    API_REPORTS_BETS_CURRENT_NORMAL: () => {
      return `/betting/${version}/eurasian/reports/bets/current/normal`;
    },
    API_REPORTS_BETS_CURRENT_LEAGUE: () => {
      return `/betting/${version}/eurasian/reports/bets/current/league`;
    },
    API_REPORTS_BETS_CURRENT_MATCH: () => {
      return `/betting/${version}/eurasian/reports/bets/current/match`;
    },
    API_REPORTS_OPEN_BETS_CURRENT_PANEL: () => {
      return `/betting/${version}/mobile/reports/bets/current/panel`;
    },
    API_REPORTS_OPEN_BETS_CURRENT_PANEL_ASIAN: () => {
      return `/betting/${version}/eurasian/reports/bets/current/panel`;
    },
    API_REPORTS_BETS_CURRENT_MATCH_LEAGUE: () => {
      return `/betting/${version}/eurasian/reports/bets/current/matchLeague/normal`;
    },
    API_REPORTS_MOBILE_TRANSACTIONS: () => {
      return `/betting/${version}/mobile/reports/transactions`;
    },
  },
};
